/**
 * Argus — Updates admin API hooks (TanStack Query).
 *
 * Bloc auto-update-agent 2026-05-06.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch, ApiError } from './api';
import { useAuthStore } from './auth';

const baseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://argus.local:8080';

export interface UpdateChannelInfo {
  name: 'dev' | 'staging' | 'prod';
  description: string | null;
  isActive: boolean;
  isFrozen: boolean;
  currentManifest: {
    manifestId: string;
    version: string;
    sizeBytes: number;
    publishedAt: string | null;
    publishedBy: string | null;
  } | null;
  agentCountPerVersion: Record<string, number>;
}

export interface UpdateManifest {
  manifestId: string;
  version: string;
  channel: 'dev' | 'staging' | 'prod';
  binaryFilename: string;
  sizeBytes: number;
  sha256: string;
  minSupportedVersion: string | null;
  isForce: boolean;
  changelog: string | null;
  publishedAt: string;
  publishedBy: string | null;
}

export interface AgentUpdateHistoryEntry {
  id: number;
  agentId: string;
  hostname: string | null;
  manifestId: string;
  fromVersion: string | null;
  toVersion: string;
  status: 'downloading' | 'verifying' | 'installing' | 'success' | 'failed' | 'rolled_back';
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
}

export function useUpdateChannels(): UseQueryResult<UpdateChannelInfo[]> {
  return useQuery({
    queryKey: ['admin', 'updateChannels'],
    queryFn: async () => {
      const res = await apiFetch<{ channels: UpdateChannelInfo[] }>('/api/v1/admin/update-channels');
      return res.data.channels;
    },
    refetchInterval: 30_000,
  });
}

export function useUpdateManifests(channel: string | null, page: number) {
  const qs = new URLSearchParams();
  if (channel) qs.set('channel', channel);
  qs.set('page', String(page));
  return useQuery({
    queryKey: ['admin', 'updateManifests', channel ?? 'all', page],
    queryFn: async () => {
      const res = await apiFetch<{ manifests: UpdateManifest[] }>(`/api/v1/admin/update-manifests?${qs}`);
      return { manifests: res.data.manifests, pagination: res.pagination };
    },
  });
}

export function useUpdateHistory(filters: { agentId?: string; status?: string }, page: number) {
  const qs = new URLSearchParams();
  if (filters.agentId) qs.set('agentId', filters.agentId);
  if (filters.status) qs.set('status', filters.status);
  qs.set('page', String(page));
  return useQuery({
    queryKey: ['admin', 'updateHistory', filters, page],
    queryFn: async () => {
      const res = await apiFetch<{ history: AgentUpdateHistoryEntry[] }>(`/api/v1/admin/update-history?${qs}`);
      return { history: res.data.history, pagination: res.pagination };
    },
    refetchInterval: 10_000,
  });
}

export function useUploadManifestMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      file: File;
      version: string;
      channel: 'dev' | 'staging' | 'prod';
      minSupportedVersion?: string;
      isForce: boolean;
      changelog?: string;
      onProgress?: (pct: number) => void;
    }) => {
      const csrfToken = useAuthStore.getState().csrfToken;
      const fd = new FormData();
      fd.append('bundle', params.file);
      fd.append('version', params.version);
      fd.append('channel', params.channel);
      fd.append('isForce', params.isForce ? 'true' : 'false');
      if (params.minSupportedVersion) fd.append('minSupportedVersion', params.minSupportedVersion);
      if (params.changelog) fd.append('changelog', params.changelog);

      // XHR pentru progress event (fetch nu suportă upload progress)
      return await new Promise<{ manifestId: string; sha256: string; sizeBytes: number }>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${baseUrl}/api/v1/admin/update-manifests`, true);
        xhr.withCredentials = true;
        if (csrfToken) xhr.setRequestHeader('X-CSRF-Token', csrfToken);
        xhr.upload.onprogress = (ev) => {
          if (ev.lengthComputable && params.onProgress) {
            params.onProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        };
        xhr.onload = () => {
          try {
            const env = JSON.parse(xhr.responseText);
            if (xhr.status >= 200 && xhr.status < 300 && env.success) {
              resolve(env.data);
            } else {
              reject(new ApiError(xhr.status, env.messages ?? [{ severity: 'error', message: `HTTP ${xhr.status}` }]));
            }
          } catch (e) {
            reject(new ApiError(xhr.status, [{ severity: 'error', message: 'Răspuns invalid' }]));
          }
        };
        xhr.onerror = () => reject(new ApiError(0, [{ severity: 'error', message: 'Network error' }]));
        xhr.send(fd);
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'updateManifests'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'updateChannels'] });
    },
  });
}

export function usePromoteMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { channel: string; manifestId: string }) => {
      const res = await apiFetch<{ promoted: boolean }>(`/api/v1/admin/update-channels/${params.channel}/promote`, {
        method: 'POST',
        body: JSON.stringify({ manifestId: params.manifestId }),
      });
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}

export function useFreezeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { channel: string; frozen: boolean }) => {
      const res = await apiFetch<{ isFrozen: boolean }>(`/api/v1/admin/update-channels/${params.channel}/freeze`, {
        method: 'POST',
        body: JSON.stringify({ frozen: params.frozen }),
      });
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'updateChannels'] });
    },
  });
}

export function useRollbackMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { channel: string; manifestId: string }) => {
      const res = await apiFetch<{ rolledBack: boolean }>(`/api/v1/admin/update-channels/${params.channel}/rollback`, {
        method: 'POST',
        body: JSON.stringify({ manifestId: params.manifestId }),
      });
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin'] });
    },
  });
}
